import React, { useState, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { fetchCountries } from "../../redux/Slices/LocationSlice";

interface RegionDropdownProps {
  countryId?: string;
  value?: string;
  onChange: (regionId: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const RegionDropdown: React.FC<RegionDropdownProps> = ({
  countryId,
  value,
  onChange,
  disabled = false,
  className = "",
  placeholder = "Select Region"
}) => {
  const dispatch = useAppDispatch();
  const { countries, countriesLoading, countriesError } = useAppSelector((state) => state.locations);
  const [isLoading, setIsLoading] = useState(false);

  // Filter regions based on selected country
  const availableRegions = useMemo(() => {
    if (!countryId || !countries) return [];
    
    const selectedCountry = countries.find(country => country.id === countryId);
    return selectedCountry?.regions || [];
  }, [countryId, countries]);

  useEffect(() => {
    if (!countries || countries.length === 0) {
      fetchCountriesData();
    }
  }, []);

  // Clear region selection when country changes
  useEffect(() => {
    if (value && !availableRegions.find(region => region.id === value)) {
      onChange("");
    }
  }, [countryId, availableRegions, value, onChange]);

  const fetchCountriesData = async () => {
    setIsLoading(true);
    try {
      await dispatch(fetchCountries({ page: 1, limit: 1000 }));
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const regionId = e.target.value;
    onChange(regionId);
  };

  return (
    <div>
      <select
        value={value || ""}
        onChange={handleChange}
        disabled={disabled || isLoading || countriesLoading || !countryId}
        className={`w-full px-4 py-2 border rounded-lg ${className}`}
      >
        <option value="">
          {!countryId 
            ? "Select a country first" 
            : isLoading || countriesLoading 
              ? "Loading regions..." 
              : availableRegions.length === 0
                ? "No regions available"
                : placeholder
          }
        </option>
        {availableRegions?.map((region) => (
          <option key={region.id} value={region.id}>
            {region.name}
          </option>
        ))}
      </select>
      {countriesError && (
        <p className="text-red-500 text-sm mt-1">Error loading regions: {countriesError}</p>
      )}
    </div>
  );
};

export default RegionDropdown;
